import { isFulfilled } from "@reduxjs/toolkit";
import { useState } from "react";
import {
    useAppDispatch as useDispatch,
    useAppSelector as useSelector,
} from "../../../../store";
import {
    hideTransparentPreloaderAction,
    showNotificationAction,
    showTransparentPreloaderAction,
} from "../../../../store/application/slice";
import { rateCandidateThunk } from "../../../../store/vacancy/thunks";
import {
    TNonRatedVacancy,
    TVacancyDetail,
} from "../../../../store/vacancy/types";
import { capitalizeName } from "../../../../utils/stringUtils";
import CustomButton from "../../../UI/Buttons/CustomButton";
import StarRating from "../../../UI/StarRating";
import ErrorMessage from "../../../UI/Tooltips/ErrorMessage";
import VacancyPosition from "../../../UI/VacancyPosition";
import { formatTimeframes } from "../../../Vacancy/timeframeApiHelper";

interface IRatingCandidateProps {
  onClose: () => void;
  vacancy?: TVacancyDetail; // single mode
  multipleCandidates?: TNonRatedVacancy[]; // multiple mode
  showConfirmBlock?: any;
}

interface Candidate {
  id: number;
  candidate_id: number;
  vacancy_id: number;
  vacancy: {
    centre: {
      id: number;
    };
  };
}

interface BulkRatingEntry {
  rating?: number;
  isBlocked?: boolean;
}

interface RatingPayload {
  candidate_id: number;
  vacancy_id: number;
  centre_id: number;
  rating: number;
  block: boolean;
}

interface BlockPayload {
  candidate_id: number;
  vacancy_id: number;
  centre_id: number;
  rating: number | null;
  block: boolean;
}

const RatingCandidate = ({
  onClose,
  vacancy,
  multipleCandidates,
  showConfirmBlock,
}: IRatingCandidateProps) => {
  const dispatch = useDispatch();
  const isMultiple = !!multipleCandidates;
  const inProgress = useSelector((state) => state.Centres.inProgress);
  const [error, setError] = useState("");
  const [rating, setRating] = useState<number>(
    vacancy?.selected_candidate?.rating?.average_rating || 0
  );
  const [isBlocked, setIsBlocked] = useState(false);
  const [bulkRatings, setBulkRatings] = useState<
    Record<
      number,
      {
        rating?: number;
        isBlocked?: boolean;
        blockReason?: string;
      }
    >
  >({});

  const formattedTimeframes = vacancy
    ? formatTimeframes(vacancy.timeframes)
    : [];

  const isSubmitDisabled = (() => {
    if (isMultiple && multipleCandidates) {
      return multipleCandidates.every(
        (c) => !bulkRatings[c.id]?.rating && !bulkRatings[c.id]?.isBlocked
      );
    } else if (vacancy) {
      return !(rating > 0 || isBlocked);
    }
    return true;
  })();

  const handleSubmit = async () => {
    setError("");

    let rawPayload: RatingPayload[] = [];
    let payload: RatingPayload[] = [];
    let blockList: BlockPayload[] = [];
    const uniqueCandidates = new Set<number>();

    if (isMultiple && multipleCandidates) {
      rawPayload = multipleCandidates
        .map((c: Candidate): RatingPayload | null => {
          const entry: BulkRatingEntry = bulkRatings[c.id] || {};
          const hasRating = entry.rating !== undefined && entry.rating > 0;
          const hasBlock = !!entry.isBlocked;

          if (!hasRating && !hasBlock) return null;

          if (hasBlock && !uniqueCandidates.has(c.candidate_id)) {
            uniqueCandidates.add(c.candidate_id);
            blockList.push({
              ...c,
              candidate_id: c.candidate_id,
              vacancy_id: c.vacancy_id,
              centre_id: c.vacancy.centre.id,
              rating: hasRating ? entry.rating! : null,
              block: true,
            });
          }

          return {
            candidate_id: c.candidate_id,
            vacancy_id: c.vacancy_id,
            centre_id: c.vacancy.centre.id,
            rating: hasRating ? entry.rating! : 0,
            block: false,
          };
        })
        .filter(Boolean) as RatingPayload[];

      // Only include rated entries
      payload = rawPayload.filter((item) => item.rating > 0);

      if (payload.length === 0 && blockList.length === 0) {
        setError("Please rate or block at least one candidate.");
        return;
      }
    } else if (vacancy) {
      const hasRating = !!rating && rating > 0;
      const hasBlock = isBlocked;

      if (!hasRating && !hasBlock) {
        setError("Please provide a rating or block the candidate.");
        return;
      }

      const candidateId = vacancy.selected_candidate.id;
      const centreId = vacancy.centre.id;
      const vacancyId = vacancy.id;

      if (hasBlock && !uniqueCandidates.has(candidateId)) {
        uniqueCandidates.add(candidateId);
        blockList.push({
          ...vacancy,
          candidate_id: candidateId,
          vacancy_id: vacancyId,
          centre_id: centreId,
          rating: hasRating ? rating : null,
          block: true,
        });
      }

      if (hasRating) {
        payload.push({
          candidate_id: candidateId,
          vacancy_id: vacancyId,
          centre_id: centreId,
          rating,
          block: false,
        });
      }
    }

    if (payload.length === 0 && blockList.length > 0) {
      showConfirmBlock(blockList);
      onClose();
      return;
    }

    try {
      dispatch(showTransparentPreloaderAction());
      const res = await dispatch(rateCandidateThunk(payload));

      if (isFulfilled(res)) {
        document.body.classList.remove("modal-open");
        dispatch(showNotificationAction("Done"));
        if (blockList.length > 0) showConfirmBlock(blockList);
        onClose();
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error(err);
      setError("Submission failed.");
    } finally {
      dispatch(hideTransparentPreloaderAction());
    }
  };

  return (
    <div className="d-flex flex-column gap-20">
      <div className="d-flex flex-column gap-15">
        <div className="fs-22 fw-400 text-black pr-15px">
          Rate Candidate Performance
        </div>
        <div className="fs-13 fw-400 text-black">
          Please rate the performance of the candidates who recently filled your
          vacancies.
        </div>
        {isMultiple && (
          <div className="fs-13 fw-400 text-black">
            If any candidate is left unrated, you will be reminded later or your
            team may complete the rating.
          </div>
        )}
      </div>
      {isMultiple && multipleCandidates
        ? multipleCandidates.map((c) => {
            const displayTimeframes = c
              ? formatTimeframes(c.vacancy.timeframes)
              : [];
            return (
              <div
                key={c.id}
                className="border border-grey-light radius-8px p-15px d-flex flex-column flex-lg-row gap-20"
              >
                <div className="w-100px h-100px radius-50px">
                  <img
                    alt="Candidate"
                    width="100px"
                    height="100px"
                    className="img-responsive rounded-circle object-fit-cover"
                    src={c?.user?.photo || ""}
                  />
                </div>
                <div className="d-flex flex-column gap-15">
                  <div className="d-flex flex-column gap-10">
                    <div className="fs-16 fw-700 text-black">{`${capitalizeName(c.user.first_name ?? "")} ${capitalizeName(c.user.last_name ?? "")}`}</div>
                    <StarRating
                      rating={bulkRatings[c.id]?.rating || 0}
                      color="#51C2B9"
                      readonly={false}
                      onChange={(val) => {
                        setBulkRatings((prev) => ({
                          ...prev,
                          [c.id]: {
                            ...prev[c.id],
                            rating: val,
                          },
                        }));
                        if (error) setError("");
                      }}
                    />
                    <div className="d-flex flex-column fs-16 fw-400 text-black">
                      <div>
                        <VacancyPosition
                          positions={c?.vacancy?.positions}
                          alternatePosition={
                            vacancy?.alternate_position_legacy || ""
                          }
                        />
                      </div>
                      <div>
                        {`${c?.vacancy?.centre?.business_name}, ${c?.vacancy?.centre?.address}`}{" "}
                        on{" "}
                        {displayTimeframes.map((ftf, i) => (
                          <span
                            key={i}
                            className={`${
                              ftf.alert ? "text-alert fw-700" : "fw-400"
                            }`}
                          >
                            {ftf.label}
                          </span>
                        ))}
                      </div>
                    </div>
                    {/* <div className="theme-check">
                    <Checkbox
                      label={`Block candidate so my centres will not be visible to them and they may not apply or accept future vacancies.`}
                      checked={isBlocked}
                      className="checked-alert"
                      onChange={(checked: boolean) => setIsBlocked(checked)}
                    />
                  </div> */}
                    <div className="theme-check">
                      <input
                        type="checkbox"
                        className="theme-check-input"
                        id={String(c.id)}
                        checked={bulkRatings[c.id]?.isBlocked || false}
                        onChange={(e) => {
                          const newBlockState = e.target.checked;
                          setBulkRatings((prev) => {
                            const updated = { ...prev };
                            multipleCandidates.forEach((cand) => {
                              if (cand.candidate_id === c.candidate_id) {
                                updated[cand.id] = {
                                  ...updated[cand.id],
                                  isBlocked: newBlockState,
                                };
                              }
                            });
                            return updated;
                          });
                          setIsBlocked(newBlockState);
                        }}
                      />
                      <label
                        htmlFor={String(c.id)}
                        className="theme-check-label fs-16 checked-alert"
                      >
                        Block candidate so my centres will not be visible to
                        them and they may not apply or accept future vacancies.
                      </label>
                    </div>
                    {/* {bulkRatings[c.id]?.isBlocked && (
                      <>
                        <div className="fs-13 fw-400 text-black">
                          Please include a reason as reference for other Centre
                          Admin and Centre Users in your team.
                        </div>
                        <div className="d-flex flex-column gap-8">
                          <TextArea
                            field={"comment"}
                            placeholder={"Enter a Reason"}
                            onChange={(e) => {
                              setBulkRatings((prev) => ({
                                ...prev,
                                [c.id]: {
                                  ...prev[c.id],
                                  blockReason: e.target.value,
                                },
                              }));
                            }}
                          />
                        </div>
                      </>
                    )} */}
                  </div>
                </div>
              </div>
            );
          })
        : vacancy && (
            <div className="border border-grey-light radius-8px p-15px d-flex gap-20">
              <div className="w-100px h-100px radius-50px">
                <img
                  alt="Candidate"
                  width="100px"
                  height="100px"
                  className="img-responsive rounded-circle object-fit-cover"
                  src={vacancy?.selected_candidate?.photo || ""}
                />
              </div>
              <div className="d-flex flex-column gap-15">
                <div className="d-flex flex-column gap-10">
                  <div className="fs-16 fw-700 text-black">{`${capitalizeName(vacancy.selected_candidate.first_name ?? "")} ${capitalizeName(vacancy.selected_candidate.last_name ?? "")}`}</div>
                  <StarRating
                    rating={rating}
                    color="#51C2B9"
                    readonly={false}
                    onChange={(val) => {
                      setRating(val);
                      if (error) setError(""); // Clear error on change
                    }}
                  />
                  <div className="d-flex flex-column fs-16 fw-400 text-black">
                    <div>
                      <VacancyPosition
                        positions={vacancy?.vacancy_positions}
                        alternatePosition={
                          vacancy?.alternate_position_legacy || ""
                        }
                      />
                    </div>
                    <div>
                      {`${vacancy?.centre?.business_name}, ${vacancy?.centre?.address}`}{" "}
                      on{" "}
                      {formattedTimeframes.map((ftf, i) => (
                        <span
                          key={i}
                          className={`${
                            ftf.alert ? "text-alert fw-700" : "fw-400"
                          }`}
                        >
                          {ftf.label}
                        </span>
                      ))}
                    </div>
                  </div>
                  {/* <div className="theme-check">
                    <Checkbox
                      label={`Block candidate so my centres will not be visible to them and they may not apply or accept future vacancies.`}
                      checked={isBlocked}
                      className="checked-alert"
                      onChange={(checked: boolean) => setIsBlocked(checked)}
                    />
                  </div> */}
                  <div className="theme-check">
                    <input
                      type="checkbox"
                      className="theme-check-input"
                      id="check_01"
                      onChange={(e) => setIsBlocked(e.target.checked)}
                    />
                    <label
                      htmlFor="check_01"
                      className="theme-check-label fs-16 checked-alert"
                    >
                      Block candidate so my centres will not be visible to them
                      and they may not apply or accept future vacancies.
                    </label>
                  </div>
                  {/* {isBlocked && (
                    <>
                      <div className="fs-13 fw-400 text-black">
                        Please include a reason as reference for other Centre
                        Admin and Centre Users in your team.
                      </div>
                      <div className="d-flex flex-column gap-8">
                        <TextArea
                          field={"comment"}
                          placeholder={"Enter a Reason"}
                          onChange={(e) => setBlockReason(e.target.value)}
                        />
                      </div>
                    </>
                  )} */}
                </div>
              </div>
            </div>
          )}

      {error && <ErrorMessage message={error} />}

      <div className="d-flex flex-column flex-lg-row gap-10 align-items-lg-center justify-content-lg-between">
        <CustomButton
          variant={"primary"}
          type="button"
          className="btn-theme bg-off-white text-black"
          onClick={onClose}
        >
          Remind me later
        </CustomButton>
        <CustomButton
          variant={"primary"}
          type="button"
          className="btn-theme bg-teal text-black"
          isLoading={inProgress}
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
        >
          Confirm and Save
        </CustomButton>
      </div>
    </div>
  );
};

export default RatingCandidate;
